import { appStore } from '@/store';
import { filterFormState } from '../../Form/utils';
import { features, getAddressMetaKey, logger } from '@/lib';
import type { NetworkStatus } from 'apollo-client';
import type { GraphQLError } from 'graphql';
import type { GenericObjectType } from '@/types';

export const mapANSRequest = (
  country: string,
): GenericObjectType | undefined => {
  logger.info('get_AddressNormalization_Payload');
  const hasAddressNormalizationShipping = features.isActive(
    'hasAddressNormalizationShipping',
  );
  const hasAddressNormalizationBoth = features.isActive(
    'hasAddressNormalizationBoth',
  );
  if (hasAddressNormalizationShipping) {
    const { api: apiState, form: formState } = appStore.getState();
    const { shippingAddress } = filterFormState(formState);
    const addressMetaKey = getAddressMetaKey(shippingAddress.country.value);
    const { layout } = apiState.griffinMetadata.address[addressMetaKey];
    const payload: GenericObjectType = {
      country: shippingAddress?.country?.value || null,
    };

    layout.reduce((accum, value) => {
      accum[value.name] = shippingAddress[value.name]?.value || null;
      return accum;
    }, payload);

    return payload;
  } else if (hasAddressNormalizationBoth) {
    const { api: apiState, form: formState } = appStore.getState();
    const { shippingAddress, billingAddress } = filterFormState(formState);
    const shippingAddressMetaKey = getAddressMetaKey(
      shippingAddress.country.value,
    );
    const billingAddressMetaKey = getAddressMetaKey(country);
    const { layout: shippingAddressLayout } =
      apiState.griffinMetadata.address[shippingAddressMetaKey];
    const { layout: billingAddressLayout } =
      apiState.griffinMetadata.address[billingAddressMetaKey];
    const payload: GenericObjectType = {
      billingcountry: country || null,
      shippingcountry: shippingAddress?.country?.value || null,
    };

    // prefixing with shipping so that its easy to map in the query
    shippingAddressLayout.reduce((accum, value) => {
      accum[`shipping${value.name}`] =
        shippingAddress[value.name]?.value || null;
      return accum;
    }, payload);

    // prefixing with billing so that its easy to map in the query
    billingAddressLayout.reduce((accum, value) => {
      accum[`billing${value.name}`] = billingAddress[value.name]?.value || null;
      return accum;
    }, payload);

    return payload;
  }
};

export const isANSResponseAlreadyNormalized = (
  country: string,
  response: {
    data: any;
    errors?: readonly GraphQLError[] | undefined;
    loading?: boolean;
    networkStatus?: NetworkStatus;
    stale?: boolean;
  },
): boolean | undefined => {
  logger.info('map_ans_response', {
    country,
  });
  const { api: apiActions } = appStore.getActions();
  const hasAddressNormalizationShipping = features.isActive(
    'hasAddressNormalizationShipping',
  );
  const hasAddressNormalizationBoth = features.isActive(
    'hasAddressNormalizationBoth',
  );
  if (hasAddressNormalizationShipping) {
    logger.info('map_shipping_ans_response', {
      country,
    });
    const { addressNormalization } = response.data;
    const { alreadyNormalized, griffinFormattedAddresses: griffin } =
      addressNormalization;

    logger.info('shipping_address_normalization_result', {
      alreadyNormalized,
      normalizedAddressFound: Boolean(griffin.formattedNormalizedAddress),
    });
    if (alreadyNormalized) {
      return true;
    }
    apiActions.setNormalizedShippingAddress(addressNormalization);
    return false;
  } else if (hasAddressNormalizationBoth) {
    logger.info('map_both_ans_response', {
      country,
    });
    const {
      billingAddress: NormalizedBillingAddress = {},
      shippingAddress: NormalizedShippingAddress = {},
    } = response.data;

    const { alreadyNormalized: billingAddressAlreadyNormalized } =
      NormalizedBillingAddress;

    const { alreadyNormalized: shippingAddressAlreadyNormalized } =
      NormalizedShippingAddress;

    // Addresses already normalized, so address doesnt need to be resolved.
    if (billingAddressAlreadyNormalized && shippingAddressAlreadyNormalized) {
      logger.info('ANS_BOTH_BILLING_SHIPPING_ADDRESSES_ALREADY_NORMALIZED', {
        country,
      });
      return true;
    }

    if (!billingAddressAlreadyNormalized && !shippingAddressAlreadyNormalized) {
      // Both addresses are not normalized, so address needs to be resolved.
      logger.info('ANS_BOTH_BILLING_SHIPPING_ADDRESSES_NOT_NORMALIZED', {
        country,
      });
      apiActions.setNormalizedShippingAddress(NormalizedShippingAddress);
      apiActions.setNormalizedBillingAddress(NormalizedBillingAddress);
    } else if (!billingAddressAlreadyNormalized) {
      // billing addresses is not normalized, so address needs to be resolved.
      logger.info('ANS_BILLING_ADDRESSES_NOT_NORMALIZED', {
        country,
      });
      apiActions.setNormalizedBillingAddress(NormalizedBillingAddress);
    } else if (!shippingAddressAlreadyNormalized) {
      // shipping addresses is not normalized, so address needs to be resolved.
      logger.info('ANS_SHIPPING_ADDRESSES_NOT_NORMALIZED', {
        country,
      });
      apiActions.setNormalizedShippingAddress(NormalizedShippingAddress);
    }
    return false;
  }
};
